import { theme, Spinner, EmptyState } from '@ketch-com/deck'
import { Box, Stack, Typography } from '@mui/material'
import { SemiCircle, SemiCircleProps } from 'components/SemiCircle'

interface RightsReport {
  pending?: number
  overdue?: number
  onTime?: number
  fulfilledSevenDays?: number
  invokedSevenDays?: number
  dueSevenDays?: number
  dueFourteenDays?: number
  onTrack?: number
  avgFulfilTime?: number
  totalInvocations?: number
}

interface SemiCircleChartProps {
  rightsReport?: RightsReport | null
  isLoading?: boolean
}

const round = (num: number) => Math.round(num)

export function SemiCircleChart({ rightsReport, isLoading = false }: SemiCircleChartProps) {
  const percentage = rightsReport ? round(((rightsReport.onTime || 0) / (rightsReport.totalInvocations || 1)) * 100) : 0

  const progressSize = Math.round(percentage / 2)

  const semiCircleProps: SemiCircleProps = {
    containerWidth: 375,
    containerHeight: 215,
    size: 375,
    value: progressSize,
    thickness: 5,
    top: 8,
    left: 3,
    color: theme.palette.Blue.Special.OceanBlue,
  }

  const hasNoData = !rightsReport || rightsReport.totalInvocations === 0

  return (
    <Box
      flexBasis={360}
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ backgroundColor: theme.palette.Common.Container }}
      borderRadius="16px"
      height="260px"
    >
      <Box px={2.5} sx={{ pt: 2 }}>
        <Typography pb={1} variant="h4">
          DSRs Fulfillment Rate
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px', mt: -12 }}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : null}

      {!isLoading && hasNoData ? (
        <Box display="flex" flexDirection="column" justifyContent="flex-end" gap={8} flex={1}>
          <Box px={2.5} display="flex" flexDirection="column" alignItems="center" gap={2} textAlign="center">
            <EmptyState
              size="small"
              colorTheme={theme.palette.Black.o48}
              iconName="FPie"
              title="No Data"
              subTitle="Adjust your search criteria or filters and try again."
            />
          </Box>

          <SemiCircle {...semiCircleProps} />
        </Box>
      ) : null}
      {!isLoading && !hasNoData ? (
        <Box position="relative" width={semiCircleProps.size} mx="auto">
          <SemiCircle {...semiCircleProps} />
          <Stack textAlign="center" spacing={1} sx={{ width: '220px', mt: -17, ml: 10 }}>
            <Typography variant="h1">{`${percentage}%`}</Typography>
            <Typography variant="label">Requests Fulfilled On Time</Typography>
            <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
              All-time percent of DSRs fulfilled within their respective timeframes
            </Typography>
          </Stack>
        </Box>
      ) : null}
    </Box>
  )
}
